import { ChevronDownIcon } from '@heroicons/react/24/outline';
import { JobPositionCardCarousel, JobPositionCardProps } from '../../legacy';
import { Heading, AccordionV2 } from '@restworld/ui-ds-base';
import { atom, useAtomValue } from 'jotai';
import { proposedJobApplicationsAtom as proposedJobApplicationsData } from '../global-state';
import { JobApplicationInterest } from './job-application-interest';
import { BASE_URL, imageVariant, useAnalytics } from '@restworld/utils-common';
import { WhyProposedApplications } from './why-proposed-applications';
import { useTranslation } from 'next-i18next';
import { useAuth } from '@restworld/feature-authentication';
import { useCallback, useRef, useState } from 'react';
import { useLoadWorkerApplications } from '../hooks/use-load-worker-applications';

export const proposedWorkerJobApplicationsAtom = atom((get) => {
  const proposedJobApplications = get(proposedJobApplicationsData);

  const proposedWorkerJobApplications = proposedJobApplications?.map((application) => {
    const job = application?.job_position;
    return {
      id: job?.id || null,
      showRestaurantDetails: job?.show_restaurant_details,
      restaurantName: job?.restaurant?.restaurant_name || '',
      city: job?.restaurant?.locality || null,
      jobTitle: job?.job_title || null,
      occupation: job?.occupation?.label_it?.label || null,
      salaryMin: job?.min_salary || null,
      salaryMax: job?.max_salary || null,
      jobType: job?.job_type_var?.label || null,
      salary: job?.salary?.toString() || null,
      restaurantLogo: imageVariant(job?.restaurant?.restaurant_logo, 'sm') || null,
      publishedAt: job?.published_at || null,
      redirectUrl: job?.semantic_unique_name ? `${BASE_URL}/posizione/${job?.semantic_unique_name}` : '#',
      jobPositionId: application?.job_position_id,
      restaurantId: application?.restaurant_id,
    };
  });
  return proposedWorkerJobApplications;
});

export const ProposedJobApplications = () => {
  const proposedJobApplications = useAtomValue(proposedWorkerJobApplicationsAtom);
  const auth = useAuth();
  const { trackEvent } = useAnalytics();
  const { fetchNextBatch } = useLoadWorkerApplications({ showLoader: false });
  const hasTriggeredFetch = useRef(false);
  const [activeSlideIndex, setActiveSlideIndex] = useState(0);

  const recordViewJobPositionEvent = useCallback(
    (jobProps: JobPositionCardProps) => {
      trackEvent('view_job', {
        email: auth?.user?.email,
        role: auth?.user?.type,
        origin: 'proposed_job_application',
        id: jobProps?.id,
        job_title: jobProps?.jobTitle,
        occupation: jobProps?.occupation,
        salary_min: jobProps?.salaryMin,
        salary_max: jobProps?.salaryMax,
        job_type: jobProps?.jobType,
        published_at: jobProps?.publishedAt,
        restaurant_name: jobProps?.restaurantName,
        city: jobProps?.city,
      });
    },
    [auth?.user?.email, auth?.user?.type, trackEvent]
  );

  const handleSlideChanged = useCallback(
    (slideIndex: number) => {
      // Update active slide index to maintain position
      setActiveSlideIndex(slideIndex);

      const totalItems = proposedJobApplications.length;
      // Calculate the trigger point: 3rd last item (n-4)
      const triggerPoint = Math.max(0, totalItems - 4);

      // Only fetch if we haven't already triggered for this batch and we're at the trigger point
      if (slideIndex >= triggerPoint && !hasTriggeredFetch.current && totalItems >= 4) {
        hasTriggeredFetch.current = true;
        fetchNextBatch('proposed');
      }

      // Reset the flag when we're back to the beginning (new batch loaded)
      if (slideIndex < triggerPoint) {
        hasTriggeredFetch.current = false;
      }
    },
    [proposedJobApplications.length, fetchNextBatch]
  );

  const jobApplicationsCardProps: JobPositionCardProps[] = proposedJobApplications.map((job) => ({
    ...job,
    cardVariant: 'job-offer',
    actionButtonsEl: (
      <JobApplicationInterest
        {...{
          jobPositionId: job.jobPositionId,
          restaurantId: job.restaurantId,
        }}
      />
    ),
    onLinkClick: () => recordViewJobPositionEvent(job),
  }));

  const CollapseIcon = (
    <div className="flex flex-col items-center justify-center  w-10 h-10 rounded-full bg-primary-300">
      <ChevronDownIcon className="w-6 h-6 text-black" />
    </div>
  );
  const { t } = useTranslation();
  if (proposedJobApplications.length === 0) return null;
  const titleText = t('job-application-page:proposed_jobs');

  return (
    <div className="flex flex-col gap-6 box-border items-start w-full max-w-full min-w-0 bg-primary-800 rounded-2xl lg:p-6 p-4">
      <AccordionV2 className="bg-none w-full overflow-hidden" open>
        <AccordionV2.Button className="w-full min-h-[48px]" icon={CollapseIcon}>
          <Heading size={3} color="white" align="left" className="hidden lg:block">
            {titleText}
          </Heading>
          <Heading size={5} color="white" align="left" className="lg:hidden text-left">
            {titleText}
          </Heading>
        </AccordionV2.Button>
        <AccordionV2.Panel className="lg:pt-2 pt-2 overflow-hidden">
          <div className="flex flex-col lg:gap-6 gap-4 items-start w-full min-w-full max-w-full">
            <JobPositionCardCarousel
              items={jobApplicationsCardProps}
              cardContainerStyling={'md:w-[390px] md:min-w-[390px] w-[327px]  min-w-[327px]'}
              cardVariant="job-proposal"
              onSlideChanged={handleSlideChanged}
              activeIndex={activeSlideIndex}
            />
            <div className="flex flex-row items-start justify-start w-full">
              <WhyProposedApplications />
            </div>
          </div>
        </AccordionV2.Panel>
      </AccordionV2>
    </div>
  );
};
